// Deterministic exporter: ProseMirror/Tiptap JSON -> source_jinja (HTML + {{ tokens }})

const escapeHtml = (raw: string): string => {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

type PMNode = {
  type: string
  text?: string
  attrs?: Record<string, any>
  marks?: Array<{ type: string; attrs?: Record<string, any> }>
  content?: PMNode[]
}

const getTextContent = (node?: PMNode): string => {
  if (!node) return ''
  if (node.type === 'text') return node.text || ''
  return (node.content || []).map(getTextContent).join('')
}

const wrapMarks = (textHtml: string, marks: PMNode['marks']): string => {
  if (!marks || !marks.length) return textHtml

  // Apply marks in order for determinism
  return marks.reduce((acc, mark) => {
    switch (mark.type) {
      case 'bold':
        return `<strong>${acc}</strong>`
      case 'italic':
        return `<em>${acc}</em>`
      case 'strike':
        return `<s>${acc}</s>`
      case 'code':
        return `<code>${acc}</code>`
      case 'link': {
        const href = mark.attrs?.href ? escapeHtml(String(mark.attrs.href)) : ''
        const target = mark.attrs?.target ? escapeHtml(String(mark.attrs.target)) : ''
        const rel = mark.attrs?.rel ? escapeHtml(String(mark.attrs.rel)) : ''
        const attrs = [`href="${href}"`, target ? `target="${target}"` : '', rel ? `rel="${rel}"` : '']
          .filter(Boolean)
          .join(' ')
        return `<a ${attrs}>${acc}</a>`
      }
      default:
        return acc
    }
  }, textHtml)
}

const renderChildren = (node?: PMNode): string => {
  return (node?.content || []).map(renderNode).join('')
}

const renderNode = (node: PMNode): string => {
  switch (node.type) {
    case 'doc':
      return (node.content || []).map(renderNode).join('\n')

    case 'paragraph': {
      const inner = renderChildren(node)
      return `<p>${inner || ''}</p>`
    }

    case 'heading': {
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level || 1)))
      return `<h${level}>${renderChildren(node)}</h${level}>`
    }

    case 'text': {
      const escaped = escapeHtml(node.text || '')
      return wrapMarks(escaped, node.marks)
    }

    case 'hardBreak':
      return '<br />'

    case 'bulletList':
      return `<ul>${renderChildren(node)}</ul>`

    case 'orderedList': {
      const start = node.attrs?.start ? Number(node.attrs.start) : undefined
      const startAttr = start && start !== 1 ? ` start="${start}"` : ''
      return `<ol${startAttr}>${renderChildren(node)}</ol>`
    }

    case 'listItem':
      return `<li>${renderChildren(node)}</li>`

    case 'variableToken': {
      const key = String(node.attrs?.key || '').trim()
      // Token is Jinja; do not escape.
      return key ? `{{ ${key} }}` : '{{ }}'
    }

    case 'table':
      return `<table><tbody>${renderChildren(node)}</tbody></table>`

    case 'tableRow':
      return `<tr>${renderChildren(node)}</tr>`

    case 'tableHeader':
      return `<th>${renderChildren(node)}</th>`

    case 'tableCell':
      return `<td>${renderChildren(node)}</td>`

    case 'codeBlock': {
      const language = node.attrs?.language ? String(node.attrs.language) : ''
      const classAttr = language ? ` class="language-${escapeHtml(language)}"` : ''
      const code = escapeHtml(getTextContent(node))
      return `<pre><code${classAttr}>${code}</code></pre>`
    }

    default:
      // Unknown nodes: try to render children to avoid data loss
      return renderChildren(node)
  }
}

class ReportTemplateExportService {
  exportToSourceJinja(editorJson: any): string {
    if (!editorJson || typeof editorJson !== 'object') return ''

    try {
      const root = editorJson as PMNode

      // If the document is a single code block, treat it as the template source.
      // This keeps Jinja templates round-trippable without wrapping them in HTML.
      if (
        root.type === 'doc' &&
        Array.isArray(root.content) &&
        root.content.length === 1 &&
        root.content[0].type === 'codeBlock'
      ) {
        const raw = getTextContent(root.content[0])
        return raw.endsWith('\n') ? raw : raw + '\n'
      }

      const html = renderNode(root)
      // Normalize trailing whitespace/newlines for stability
      return String(html).trim() + '\n'
    } catch (e) {
      return ''
    }
  }
}

export default new ReportTemplateExportService()
